import type { UNKNOWN } from "@src/components/vGrid/VGrid";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { jwtDecode } from "jwt-decode";
import { Environment, getCurrentEnvironment } from "@src/utils/environment.utils";

export const snowFlakeBaseUrl = "https://sf-pricing-tool-service.azurewebsites.net/api/v1";

export type EnvironmentType = "PROD" | "UAT" | "QA";
export class ApiProvider {
	private static instance: ApiProvider;
	private axiosInstance: AxiosInstance;
	private _environment: EnvironmentType = getCurrentEnvironment() === Environment.PROD ? "PROD" : "QA";
	public setEnviroment(env: EnvironmentType) {
		this._environment = env;
		this.Initialize();
	}
	public get environment(): EnvironmentType {
		if (this.token) {
			try {
				return jwtDecode<{ Environment: EnvironmentType }>(this.token).Environment;
			} catch {
				return this._environment;
			}
		}
		return this._environment;
	}
	public static baseURLFor(environment: EnvironmentType) {
		switch (environment) {
			case "PROD":
				return process.env.PROD_API_BASE_URL ?? "";
			case "UAT":
				return process.env.UAT_API_BASE_URL ?? "";
			case "QA":
			default:
				return process.env.QA_API_BASE_URL ?? "";
		}
	}
	public get baseURL() {
		return ApiProvider.baseURLFor(this.environment);
	}
	public get passwordManagerBaseURL() {
		switch (this.environment) {
			case "PROD":
			case "UAT":
				return process.env.PROD_PASSWORD_MANAGER_API_BASE_URL ?? "";
			case "QA":
				return process.env.QA_PASSWORD_MANAGER_API_BASE_URL ?? "";
			default:
				return process.env.QA_PASSWORD_MANAGER_API_BASE_URL ?? "";
		}
	}
	public get autoPoBaseURL() {
		switch (this.environment) {
			case "PROD":
			case "UAT":
				return process.env.PROD_AUTOPO_API_BASE_URL ?? "";
			case "QA":
			default:
				return process.env.QA_AUTOPO_API_BASE_URL ?? "";
		}
	}
	private token = "";
	private cache = new Map<string, Map<string, unknown>>();
	setCache<T>(path: string, key: string, data: T) {
		if (!key) return;
		if (!this.cache.has(path)) this.cache.set(path, new Map<string, unknown>());
		this.cache.get(path)?.set(key, data);
	}
	getCache<T>(path: string, key: string): T | undefined {
		if (!key) return;
		if (!this.cache.has(path)) this.cache.set(path, new Map<string, unknown>());
		return this.cache.get(path)?.get(key) as T;
	}
	removeCache(path: string, key: string) {
		if (!key) return;
		this.cache.get(path)?.delete(key);
	}
	removeCachePath(path: string) {
		this.cache.delete(path);
	}
	getAllCache() {
		return this.cache;
	}
	resetCache() {
		this.cache = new Map<string, Map<string, unknown>>();
	}
	private Initialize() {
		this.axiosInstance = axios.create({
			baseURL: this.baseURL,
			headers: {
				"Content-Type": "application/json",
				Accept: "/",
				"Cache-Control": "no-cache",
			},
		});

		this.axiosInstance.interceptors.request.use(
			(config) => {
				if (this.token) {
					config.headers.Authorization = `Bearer ${this.token}`;
				}
				return config;
			},
			(error) => {
				return Promise.reject(new Error(error));
			},
		);

		this.axiosInstance.interceptors.response.use(
			(response) => response,
			(error) => {
				// ApiProvider uses its own axios instance (not the default),
				// so the global interceptor doesn't cover it.
				if (error?.response?.status === 401) {
					// Import is avoided here; we read the flag via the module
					// export at call-time.  The event tells AuthProvider to
					// redirect; toast suppression relies on the flag.
					window.dispatchEvent(new CustomEvent("auth:session-expired"));
				}
				return Promise.reject(error);
			},
		);

		return this.axiosInstance;
	}
	private constructor() {
		this.axiosInstance = this.Initialize();
	}

	public static get default(): ApiProvider {
		ApiProvider.instance = ApiProvider.instance ?? new ApiProvider();
		return ApiProvider.instance;
	}

	public setToken(token: string): void {
		this.token = token;
		this.Initialize();
	}

	public removeToken(): void {
		this.token = "";
	}

	public get axios(): AxiosInstance {
		return this.axiosInstance;
	}

	private objectToQueryString(obj?: object) {
		if (!obj) return "";
		const serializeValue = (value: unknown) => {
			if (value instanceof Date) return value.toISOString();
			if (value instanceof Boolean) return value ? "true" : "false";
			return `${value}`;
		};
		return Object.entries(obj)
			.flatMap(([key, value]) => {
				if (Array.isArray(value)) {
					return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(serializeValue(v))}`);
				}
				return [`${encodeURIComponent(key)}=${encodeURIComponent(serializeValue(value))}`];
			})
			.join("&");
	}
	private appendQueryStringToUrl(url: string, queryString: string) {
		if (!queryString) return url;
		return url.includes("?")
			? `${url}&${queryString}&_dc=${new Date().getTime()}`
			: `${url}?${queryString}&_dc=${new Date().getTime()}`;
	}
	public async get<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.axiosInstance.get<T>(this.appendQueryStringToUrl(url, this.objectToQueryString(data)), config);
		return response.data;
	}
	public async getAnonymous<T>(url: string, environment: EnvironmentType, data?: object): Promise<T> {
		const response = await axios.get<T>(this.appendQueryStringToUrl(url, this.objectToQueryString(data)), {
			baseURL: ApiProvider.baseURLFor(environment),
		});
		return response.data;
	}
	public async getFile<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<string> {
		const response = await this.axiosInstance.get<T>(this.appendQueryStringToUrl(url, this.objectToQueryString(data)), {
			...config,
			responseType: "arraybuffer",
		});
		const blob = new Blob([response.data as BlobPart], { type: "image/png" });
		const imageUrl = URL.createObjectURL(blob);
		return imageUrl;
	}
	public async postWithHeaders<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.axiosInstance.post<T>(`${url}?_dc=${new Date().getTime()}`, data, config);
	}
	public async post<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.postWithHeaders<T>(url, data, config);
		return response.data;
	}
	public async postWithFile<T>(url: string, data?: Record<string, UNKNOWN>): Promise<T> {
		const formData = new FormData();
		if (data) {
			Object.keys(data).forEach((key) => {
				formData.append(key, data[key]);
			});
		}

		const response = await this.axiosInstance.post<T>(`${url}?_dc=${new Date().getTime()}`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		return response.data;
	}
	public async postWithFilePdf<T>(url: string, data?: Record<string, UNKNOWN>): Promise<T> {
		const formData = new FormData();
		if (data) {
			Object.keys(data).forEach((key) => {
				formData.append(key, data[key]);
			});
		}

		const response = await this.axiosInstance.post<T>(`${url}?_dc=${new Date().getTime()}`, formData, {});

		return response.data;
	}
	public async delete<T>(url: string, data?: object): Promise<T> {
		const response = await this.axiosInstance.delete<T>(`${url}?_dc=${new Date().getTime()}`, {
			data,
		});
		return response.data;
	}
	public async put<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.axiosInstance.put<T>(`${url}?_dc=${new Date().getTime()}`, data, config);
		return response.data;
	}
}
